import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Web mirror of Android's BillingPdfRenderer: A4 portrait, project title, the
// generated date, the human (+AI) rate, the date range + labels, a
// Task/Description/Hours/Amount table, totals on the last page, and page
// numbers. Triggers a browser download.

function fmtMoney(symbol, amount) {
  return `${symbol}${Number(amount).toFixed(2)}`;
}
function fmtDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function sanitize(name) {
  return (name || "report").replace(/[^A-Za-z0-9]+/g, "_").slice(0, 80) || "report";
}

export function generateBillingPdf({
  projectName,
  currencySymbol = "$",
  rate = 0,
  aiRate = 0,
  rows = [],
  startDate = null,
  endDate = null,
  labelNames = [],
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 40;
  let y = margin;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(projectName, margin, y);
  y += 24;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Generated: ${fmtDate(new Date())}`, margin, y);
  y += 14;
  const rateLine =
    aiRate > 0
      ? `Rate: ${fmtMoney(currencySymbol, rate)} / hour (Human)  ·  ${fmtMoney(currencySymbol, aiRate)} / hour (AI)`
      : `Rate: ${fmtMoney(currencySymbol, rate)} / hour`;
  doc.text(rateLine, margin, y);
  y += 14;
  if (startDate && endDate) {
    doc.text(`Range: ${fmtDate(startDate)} – ${fmtDate(endDate)}`, margin, y);
    y += 14;
  }
  if (labelNames.length) {
    doc.text(`Labels: ${labelNames.join(", ")}`, margin, y);
    y += 14;
  }
  y += 8;

  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  const totalAmount = rows.reduce((s, r) => s + r.amount, 0);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Task", "Description", "Hours", "Amount"]],
    body: rows.map((r) => [
      r.kindLabel ? `${r.kindLabel} · ${r.taskTitle}` : r.taskTitle,
      r.description || "",
      r.hours.toFixed(2),
      fmtMoney(currencySymbol, r.amount),
    ]),
    foot: [["", "Total", totalHours.toFixed(2), fmtMoney(currencySymbol, totalAmount)]],
    showFoot: "lastPage",
    columnStyles: {
      0: { cellWidth: 130 },
      1: { cellWidth: 215 },
      2: { cellWidth: 50, halign: "right" },
      3: { cellWidth: 90, halign: "right" },
    },
    styles: { font: "helvetica", fontSize: 10, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fontStyle: "bold", fillColor: [238, 238, 238], textColor: 30 },
    footStyles: { fontStyle: "bold", fillColor: [238, 238, 238], textColor: 30 },
  });

  const pageCount = doc.internal.getNumberOfPages();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`Page ${i} of ${pageCount}`, margin, pageH - 20);
  }

  doc.save(`${sanitize(projectName)}-${Date.now()}.pdf`);
}
